"use client";

import {
  CloseButton,
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import clsx from "clsx";
import { FC, Fragment, HTMLAttributes, PropsWithChildren } from "react";
import Icon from "../Icon";

export interface AppModalProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "role"> {
  isOpen: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  className?: string;
  width?: number | string;
  children?: any;
}

export interface ModalTitleProps {
  closable?: boolean;
}

const ModalTitle: FC<PropsWithChildren<ModalTitleProps>> = ({
  children,
  closable,
  ...props
}) => {
  return (
    <DialogTitle
      {...props}
      className="px-3s py-3xs text-app-h3 border-b border-b-neutral-50 flex justify-between items-center sticky top-none gap bg-neutral-0"
    >
      {children}
      {closable && (
        <CloseButton className="flex items-center text-neutral-675/60 hover:text-neutral-675 focusable">
          <Icon type="Close" size="20" />
        </CloseButton>
      )}
    </DialogTitle>
  );
};

const ModalDescription: FC<any> = (props) => {
  return <Dialog.Description {...props} className="px-3s" />;
};

export interface ModalContentProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
}

const ModalContent: FC<PropsWithChildren<ModalContentProps>> = ({
  children,
  className,
  ...props
}) => {
  return (
    <div {...props} className={clsx("px-3s", className)}>
      {children}
    </div>
  );
};

export interface ModalFooterProps {
  className?: string;
}

const ModalFooter: FC<PropsWithChildren<ModalFooterProps>> = ({
  children,
  className,
}) => {
  return (
    <div className={clsx("px-3s flex gap-3xs justify-end", className)}>
      {children}
    </div>
  );
};

const AppModal = ({
  isOpen,
  children,
  className,
  width,
  onOpenChange,
  ...props
}: AppModalProps) => {
  return (
    <Transition appear show={isOpen} as={Fragment as any}>
      <Dialog onClose={() => onOpenChange && onOpenChange(false)}>
        <TransitionChild
          as={Fragment as any}
          enter="ease-out duration-150"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className={clsx(
              "fixed w-screen h-screen flex justify-center items-center bg-neutral-875 bg-opacity-25 top-none left-none z-50",
            )}
          ></div>
        </TransitionChild>

        <div className="fixed inset-none flex h-screen-safe items-center justify-center z-50">
          <TransitionChild
            as={Fragment as any}
            enter="ease-out duration-150"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <div className="flex flex-1 justify-center">
              <DialogPanel
                style={{ maxWidth: width, width }}
                className={clsx(
                  "bg-neutral-0 flex flex-col gap overflow-auto  m:max-h-[calc(100vh-32px)] max-w-[700px] m-s rounded-2xs shadow-3 pb-3s",
                  className,
                )}
                {...props}
              >
                {children}
              </DialogPanel>
            </div>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  );
};

export default Object.assign(AppModal, {
  Title: ModalTitle,
  Description: ModalDescription,
  Content: ModalContent,
  Footer: ModalFooter,
});
